import { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { InvoicePaymentRow } from './types';
import { exportInvoicePaymentsToXlsx, exportInvoicePaymentsToCsv } from './paymentsExportUtils';
import { Export, Eye } from '@phosphor-icons/react';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

// ─── Column helpers ───────────────────────────────────────────────────────────

export const INVOICE_PAYMENT_COLUMN_LABELS: Record<keyof InvoicePaymentRow, string> = {
  paymentId: 'Payment ID',
  invoiceId: 'Invoice ID',
  invoiceNumber: 'Invoice Number',
  amountPaid: 'Amount Paid',
  discountApplied: 'Discount Applied',
  amount: 'Invoice Amount',
  createdAt: 'Created At',
  customerId: 'Customer ID',
  customerName: 'Customer Name',
  creditUsed: 'Credit Used',
  notes: 'Notes',
};

const textColumn = (
  key: keyof InvoicePaymentRow,
  label: string,
  options?: { defaultHidden?: boolean; width?: number }
): DillColumn<InvoicePaymentRow> => ({
  key,
  label,
  width: options?.width ?? 140,
  sortable: true,
  filterable: true,
  toggleable: true,
  defaultHidden: options?.defaultHidden,
  render: (value: string) => <div className="text-xs text-gray-500">{value || '—'}</div>,
});

const numericColumn = (
  key: keyof InvoicePaymentRow,
  label: string,
  width = 150
): DillColumn<InvoicePaymentRow> => ({
  key,
  label,
  width,
  sortable: true,
  filterable: true,
  numeric: true,
  toggleable: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        {value < 0 ? '-' : ''}$
        {Math.abs(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

// ─── Column definitions ────────────────────────────────────────────────────────

const getInvoicePaymentsColumns = (): DillColumn<InvoicePaymentRow>[] => [
  textColumn('paymentId', 'Payment ID', { defaultHidden: true, width: 220 }),
  textColumn('invoiceId', 'Invoice ID', { defaultHidden: true, width: 240 }),
  textColumn('invoiceNumber', 'Invoice #', { width: 130 }),
  numericColumn('amountPaid', 'Amount Paid', 130),
  numericColumn('discountApplied', 'Discount', 120),
  numericColumn('amount', 'Invoice Amount', 140),
  {
    key: 'createdAt',
    label: 'Created At',
    width: 120,
    sortable: true,
    filterable: true,
    toggleable: true,
    render: (value: string) => {
      if (!value) return <div className="text-xs text-gray-500">—</div>;
      return <div className="text-xs text-gray-500">{moment(value).format('MM/DD/YYYY')}</div>;
    },
  },
  textColumn('customerId', 'Customer ID', { width: 130 }),
  textColumn('customerName', 'Customer Name', { width: 180 }),
  textColumn('creditUsed', 'Credit Used', { width: 130 }),
  textColumn('notes', 'Notes', { width: 130 }),
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface InvoicePaymentsTableProps {
  data: InvoicePaymentRow[];
  isLoading?: boolean;
}

export const InvoicePaymentsTable = ({ data, isLoading }: InvoicePaymentsTableProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Rows after the table's search/filters/sorting are applied — what we export.
  const [filteredData, setFilteredData] = useState<InvoicePaymentRow[]>(data);
  const exportRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  const allColumns = useMemo(() => getInvoicePaymentsColumns(), []);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(
    () => new Set(allColumns.filter((c) => c.defaultHidden).map((c) => c.key))
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hiddenCols.has(c.key)),
    [allColumns, hiddenCols]
  );

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        const visibleCount = allColumns.filter((c) => !next.has(c.key)).length;
        if (visibleCount <= 3) return prev;
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setIsColumnsOpen(false);
      }
    };

    if (isExportOpen || isColumnsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen, isColumnsOpen]);

  const handleExportXlsx = () => {
    exportInvoicePaymentsToXlsx(
      filteredData,
      INVOICE_PAYMENT_COLUMN_LABELS,
      visibleColumns.map((c) => c.key as keyof InvoicePaymentRow)
    );
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportInvoicePaymentsToCsv(
      filteredData,
      INVOICE_PAYMENT_COLUMN_LABELS,
      visibleColumns.map((c) => c.key as keyof InvoicePaymentRow)
    );
    setIsExportOpen(false);
  };

  return (
    <div className="p-6 flex flex-col">
      <div className="mb-4">
        <p className="text-xs text-gray-500">
          Export invoice payment details including invoice numbers, amounts paid, and discounts
          applied. Use this to reconcile payments against your ERP system.
        </p>
      </div>

      <DillTable<InvoicePaymentRow>
        columns={visibleColumns}
        data={data}
        onProcessedDataChange={setFilteredData}
        rowKey={(r) => `${r.paymentId}-${r.invoiceId}`}
        verticalDividers
        search
        searchPlaceholder="Search invoice payments…"
        pagination
        pageSize={25}
        rowHeight={35}
        scrollX
        showActiveFilters
        striped={false}
        maxHeight="calc(100vh - 280px)"
        loading={isLoading}
        loadingText="Loading invoice payments..."
        emptyMessage="No invoice payments match your filters"
        showCount={false}
        toolbarRight={
          <div className="flex gap-2">
            {/* Column visibility dropdown */}
            <div className="relative" ref={columnsRef}>
              <DillButton
                label="Columns"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                icon={<Eye size={14} />}
                variant="ghost"
              />
              {isColumnsOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 w-56 max-h-[400px] overflow-y-auto">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 flex items-center justify-between">
                    <span>Show/Hide Columns</span>
                    <button
                      onClick={() => setHiddenCols(new Set())}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 normal-case tracking-normal">
                      Select All
                    </button>
                  </div>
                  {allColumns.map((col) => {
                    const isVisible = !hiddenCols.has(col.key);
                    const canHide = isVisible && visibleColumns.length <= 3;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 ${
                          canHide ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumn(col.key)}
                          disabled={canHide}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-100/80 disabled:opacity-50"
                        />
                        <span className="flex-1">{col.label}</span>
                        {canHide && (
                          <span className="text-[10px] text-gray-400">Min 3 required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              className="relative"
              ref={exportRef}
              title={isLoading ? 'Still loading invoice payments…' : undefined}>
              <DillButton
                label="Export"
                onClick={() => setIsExportOpen(!isExportOpen)}
                icon={<Export size={14} />}
                variant="primary"
                isDisabled={isLoading}
              />
              {isExportOpen && (
                <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                  <button
                    onClick={handleExportXlsx}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as XLSX
                  </button>
                  <button
                    onClick={handleExportCsv}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                    Export as CSV
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};
